import { absoluteUrl } from "@/lib/site-origin";
import { fitTitle, fitDescription } from "@/lib/seo-head";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { stateKeywordsMeta } from "@/lib/page-keywords";
import { getProduct } from "@/data/products";
import { getProductHero } from "@/data/productHeroes";
import { getStateHero } from "@/data/state-heroes";
import { getState } from "@/data/states";
import { getMarketStats } from "@/lib/market-stats.functions";
import { getHousingMomentum } from "@/lib/housing-momentum.functions";
import { getCountyInsights } from "@/lib/county-insights.functions";
import { ConstructionStatePage } from "@/components/site/ConstructionStatePage";
import { constructionStateFaqJsonLd, constructionStateMetaDescription } from "@/components/site/ConstructionStatePage.seo";
import { breadcrumbJsonLd, productHowToJsonLd, productArticleJsonLd } from "@/components/site/ProductPage.seo";
import { localBusinessJsonLd, serviceJsonLd, speakableJsonLd } from "@/lib/seo-schema";

const SLUG = "construction-loans";
const PRODUCT = getProduct(SLUG)!;
const HERO = getProductHero(SLUG)!;

export const Route = createFileRoute("/construction-loans/$state/")({
  loader: async ({ params }) => {
    const state = getState(params.state);
    if (!state) throw notFound();
    const [liveStats, housingMomentum, countyInsights] = await Promise.all([
      getMarketStats({ data: { stateSlug: params.state } }).catch(() => null),
      getHousingMomentum({ data: { stateAbbrev: state.abbrev, stateName: state.name } }).catch(() => null),
      getCountyInsights({ data: { stateAbbrev: state.abbrev, placeLabel: state.name } }).catch(() => null),
    ]);
    return { state, liveStats, housingMomentum, countyInsights };
  },
  head: ({ params, loaderData }) => {
    const state = loaderData?.state;
    if (!state) return { meta: [{ title: `Construction loans | Simply Approved` }] };
    const url = `/${SLUG}/${params.state}`;
    const stateHero = getStateHero(params.state) ?? HERO;
    const title = fitTitle(`${state.name} Construction Loans | Ground-Up Builder & Spec Home Financing in ${state.name} | Simply Approved`);
    const description = fitDescription(constructionStateMetaDescription(state));
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "keywords", content: stateKeywordsMeta(PRODUCT.slug, state.name) },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "product" },
        { property: "og:url", content: `https://dscrloanmarket.com${url}` },
        { property: "og:image", content: absoluteUrl(stateHero.src) },
        { property: "og:image:alt", content: stateHero.alt },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: absoluteUrl(stateHero.src) },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "robots", content: "index, follow, max-image-preview:large" },
      ],
      links: [
        { rel: "canonical", href: `https://dscrloanmarket.com${url}` },
        { rel: "alternate", hrefLang: "en-us", href: `https://dscrloanmarket.com${url}` },
        { rel: "preload", as: "image", href: stateHero.src },
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(localBusinessJsonLd({ url, state: state.name })) },
        { type: "application/ld+json", children: JSON.stringify(serviceJsonLd({ url, serviceName: `Ground-Up Construction Loans in ${state.name}`, description, state: state.name })) },
        { type: "application/ld+json", children: JSON.stringify(speakableJsonLd(url)) },
        { type: "application/ld+json", children: JSON.stringify(constructionStateFaqJsonLd(state)) },
        { type: "application/ld+json", children: JSON.stringify(productHowToJsonLd(PRODUCT, url)) },
        { type: "application/ld+json", children: JSON.stringify(productArticleJsonLd(PRODUCT, url)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbJsonLd([
              { name: "Home", url: "/" },
              { name: PRODUCT.shortName, url: `/${SLUG}` },
              { name: state.name, url },
            ]),
          ),
        },
      ],
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { state, liveStats, housingMomentum, countyInsights } = Route.useLoaderData();
  return (
    <ConstructionStatePage product={PRODUCT} state={state} liveStats={liveStats} housingMomentum={housingMomentum} countyInsights={countyInsights} midSlot={<ProductReportCtaBanner productKey="construction" variant="full" locationLabel={state.name} />} />
  );
}
